/**
 * Centralised HTML sanitization — DOMPurify wrapper.
 *
 * Use anywhere we need to inject untrusted (or "admin-trusted" but stored)
 * HTML into the DOM via `dangerouslySetInnerHTML`.
 *
 * DOMPurify blocks ALL of:
 *   • <script>, <iframe>, <embed>, <object>, <link>, <meta>, <form>
 *   • on* event-handler attributes (onclick, onerror, etc.)
 *   • javascript:, data:, vbscript: URL schemes
 *   • <svg> with executable payloads
 *   • Encoded / nested bypass attempts (<scri<script>pt>)
 *
 * Two presets are exported:
 *   sanitizeRichText  → safe for product descriptions / CMS bodies
 *                       (allows p, span, strong, em, ul, ol, li, a, br, img…)
 *   sanitizeMinimal   → strict whitelist for inline snippets
 */
import DOMPurify from "dompurify";

const RICH_TEXT_CONFIG = {
  ALLOWED_TAGS: [
    "p", "span", "div", "br", "hr",
    "strong", "b", "em", "i", "u", "s",
    "ul", "ol", "li",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "a", "img",
    "blockquote", "code", "pre",
    "table", "thead", "tbody", "tr", "td", "th",
  ],
  ALLOWED_ATTR: ["href", "title", "alt", "src", "target", "rel", "class", "style"],
  // Hard-block dangerous URL schemes — only http/https/mailto/tel
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[#/])/i,
  // Force external links to be safe
  ADD_ATTR: ["target"],
};

const MINIMAL_CONFIG = {
  ALLOWED_TAGS: ["b", "strong", "i", "em", "u", "br", "span"],
  ALLOWED_ATTR: [],
};

export function sanitizeRichText(dirty) {
  if (!dirty || typeof dirty !== "string") return "";
  return DOMPurify.sanitize(dirty, RICH_TEXT_CONFIG);
}

export function sanitizeMinimal(dirty) {
  if (!dirty || typeof dirty !== "string") return "";
  return DOMPurify.sanitize(dirty, MINIMAL_CONFIG);
}

export default sanitizeRichText;
